import React, { useRef } from 'react';

// L'ALBERO DEL COMPOSER DA TASTIERA (utente, 2026-10-22, accessibilità; schema «tree» di WAI-ARIA). Le righe sono di tre
// componenti diversi (attributi, opzioni, aree), quindi la tastiera si governa da chi le contiene, guardando lo
// schermo: le righe si contano nell'ordine in cui si vedono.
//   - L'albero è UNA fermata del Tab: si entra sulla riga col fuoco di prima, o sulla scelta, o sulla prima. Dentro una
//     riga il Tab passa ai suoi comandi; dopo l'ultimo esce dall'albero, invece di proseguire nella riga sotto.
//   - Su e Giù passano di riga, Home e Fine vanno ai capi. Destra apre un ramo o scende al primo figlio, Sinistra lo
//     chiude o sale al genitore. Alt+Su/Giù restano a spostare la riga.
//   - Invio e Spazio sono il clic (Ctrl/⌘+Spazio aggiunge alla scelta); Invio su una riga già scelta è il doppio clic,
//     che apre ciò che il doppio clic apre.

const ROW = '[role="treeitem"]';
const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const visible = (element: HTMLElement) => element.offsetParent !== null;
const isKeyboardFocus = (element: Element) => {
    try {
        return element.matches(':focus-visible');
    } catch {
        return false;
    }
};
const focusablesIn = (root: ParentNode) => Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(visible);

const pointer = (row: HTMLElement, type: 'click' | 'dblclick', event: React.KeyboardEvent) =>
    row.dispatchEvent(new MouseEvent(type, { bubbles: true, cancelable: true, ctrlKey: event.ctrlKey, metaKey: event.metaKey }));

export const useComposerTreeKeyboard = () => {
    const treeRef = useRef<HTMLDivElement>(null);
    // La riga che ha avuto il fuoco per ultima: è lì che si rientra.
    const lastRowId = useRef<string | null>(null);

    const rows = () => Array.from(treeRef.current?.querySelectorAll<HTMLElement>(ROW) ?? []).filter(visible);

    const tabStop = () => {
        const all = rows();
        return (
            all.find((row) => row.getAttribute('data-composer-row') === lastRowId.current) ??
            all.find((row) => row.getAttribute('aria-selected') === 'true') ??
            all[0]
        );
    };

    // Esce dall'albero verso il primo comando dopo (o prima) di lui nella pagina.
    const leaveTree = (forward: boolean) => {
        const tree = treeRef.current;
        if (!tree) return;
        const page = focusablesIn(document);
        const inside = page.filter((element) => tree.contains(element));
        if (!inside.length) return;
        const edge = page.indexOf(forward ? inside[inside.length - 1] : inside[0]);
        page[forward ? edge + 1 : edge - 1]?.focus();
    };

    const onFocus = (event: React.FocusEvent<HTMLDivElement>) => {
        const tree = treeRef.current;
        const target = event.target as HTMLElement;
        const row = target.closest<HTMLElement>(ROW);

        // ENTRANDO DA FUORI CON LA TASTIERA si arriva sulla riga, non sul comando di una riga qualsiasi. Si cerca la
        // fermata PRIMA di ricordare la riga appena toccata, altrimenti si rientrava sempre sulla prima (o l'ultima).
        // Col mouse (il fuoco non è :focus-visible) il comando cliccato tiene il fuoco: spostarlo sulla riga poteva
        // far scorrere l'albero sotto il puntatore e mandare il clic altrove.
        const from = event.relatedTarget as Node | null;
        if (tree && !(from && tree.contains(from)) && isKeyboardFocus(target)) {
            const stop = tabStop();
            if (stop && target !== stop && !target.matches('input, textarea')) {
                // Il fuoco sulla fermata ripassa di qui, da dentro l'albero, e ricorda quella riga.
                stop.focus();
                return;
            }
        }
        if (row) lastRowId.current = row.getAttribute('data-composer-row');
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const tree = treeRef.current;
        const target = event.target as HTMLElement;
        if (!tree) return;
        const row = target.closest<HTMLElement>(ROW);

        if (event.key === 'Tab' && row) {
            // Il Tab resta nella riga col fuoco; quando uscirebbe verso un'altra riga, esce dall'albero.
            const all = focusablesIn(tree);
            const next = all[all.indexOf(target) + (event.shiftKey ? -1 : 1)];
            if (next && next.closest(ROW) === row) return;
            if (event.shiftKey && target === row) {
                event.preventDefault();
                leaveTree(false);
            } else if (!event.shiftKey) {
                event.preventDefault();
                leaveTree(true);
            } else {
                event.preventDefault();
                row.focus();
            }
            return;
        }

        // Le frecce e Invio valgono sulla riga, non sui suoi comandi né nel nome aperto in scrittura.
        if (target !== row || event.altKey) return;
        const all = rows();
        const index = all.indexOf(row);
        const go = (next?: HTMLElement) => {
            event.preventDefault();
            next?.focus();
        };
        const expanded = row.getAttribute('aria-expanded');
        // APRIRE O CHIUDERE RIDISEGNA LA RIGA, e il fuoco cadeva sulla pagina: lo si rimette sulla riga ridisegnata.
        const toggleBranch = () => {
            const id = row.getAttribute('data-composer-row');
            row.querySelector<HTMLElement>('[data-tree-twisty]')?.click();
            requestAnimationFrame(() => tree.querySelector<HTMLElement>(`${ROW}[data-composer-row="${id}"]`)?.focus());
        };

        switch (event.key) {
            case 'ArrowDown':
                return go(all[index + 1]);
            case 'ArrowUp':
                return go(all[index - 1]);
            case 'Home':
                return go(all[0]);
            case 'End':
                return go(all[all.length - 1]);
            case 'ArrowRight':
                if (expanded === 'false') {
                    event.preventDefault();
                    toggleBranch();
                } else if (expanded === 'true') go(all[index + 1]);
                return;
            case 'ArrowLeft': {
                if (expanded === 'true') {
                    event.preventDefault();
                    toggleBranch();
                    return;
                }
                // Il genitore è la riga disegnata subito prima del gruppo che contiene questa: i gruppi e gli step
                // stanno in un frammento senza involucro, quindi il primo ROW del contenitore sarebbe un'altra riga.
                // La riga può essere avvolta nella sua testata appiccicosa (StickyRow).
                const group = row.parentElement?.closest<HTMLElement>('[role="group"]');
                const before = group?.previousElementSibling as HTMLElement | null | undefined;
                return go((before?.matches(ROW) ? before : before?.querySelector<HTMLElement>(ROW)) ?? undefined);
            }
            case 'Enter':
                event.preventDefault();
                pointer(row, row.getAttribute('aria-selected') === 'true' ? 'dblclick' : 'click', event);
                return;
            case ' ':
                event.preventDefault();
                pointer(row, 'click', event);
                return;
        }
    };

    return { treeRef, onFocus, onKeyDown };
};
